import { useState } from "react";
import { Avatar, Button, EmptyState, Modal, Spinner } from "@cntrl/ui";
import { ApiError } from "../../app/hooks/apiError.js";
import {
  useAcceptInvite,
  useDeclineInvite,
  useMyInvites,
  type MyInvite,
} from "../../app/hooks/useMyInvites.js";
import { pushToast } from "../../app/toasts.js";
import { inviteSentence, tenantInitials } from "./inviteCopy.js";
import styles from "./PendingInvitesModal.module.scss";

export interface PendingInvitesModalProps {
  onClose: () => void;
}

/** "Expires Oct 15" — wall-clock local. */
function expiresLine(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? null
    : `Expires ${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
}

/**
 * Pending invites (ART-239) — every invite addressed to the signed-in person, across every handshake (crew, org
 * team, partner, org link), each with Accept / Decline. Opened from the account dropdown (present only when there
 * are any) and from a `seat_invite` bell row or push.
 *
 * It replaces `IncomingConnectionsInbox`, which was the only accept surface and had been unmounted by the dashboard
 * and roster-home rebuilds — so between 2026-08-23 and this, nobody could accept a seat invite at all.
 */
export function PendingInvitesModal({ onClose }: PendingInvitesModalProps) {
  const invites = useMyInvites();
  const rows = invites.data?.items ?? [];

  return (
    <Modal title="Pending invites" size="md" onClose={onClose}>
      {invites.isLoading ? (
        <div className={styles.center} role="status" aria-live="polite">
          <Spinner />
        </div>
      ) : invites.isError ? (
        <EmptyState
          title="Couldn't load your invites"
          description={invites.error instanceof ApiError ? invites.error.message : "Please try again."}
          action={<Button onClick={() => void invites.refetch()}>Retry</Button>}
        />
      ) : rows.length === 0 ? (
        <EmptyState title="No pending invites" description="When someone invites you to a band or a team, it shows up here." />
      ) : (
        <ul className={styles.rows}>
          {rows.map((invite) => (
            <InviteRow key={invite.inviteId} invite={invite} />
          ))}
        </ul>
      )}
    </Modal>
  );
}

function InviteRow({ invite }: { invite: MyInvite }) {
  const accept = useAcceptInvite();
  const decline = useDeclineInvite();
  const [error, setError] = useState<string | null>(null);
  const inviteId = invite.inviteId!;
  const accepting = accept.isPending && accept.variables === inviteId;
  const declining = decline.isPending && decline.variables === inviteId;
  const busy = accepting || declining;
  const sentence = inviteSentence(invite);
  const expires = expiresLine(invite.expiresOn);

  function onAccept() {
    setError(null);
    accept.mutate(inviteId, {
      onSuccess: () =>
        pushToast(invite.kind === "partner" ? "You're connected." : `You joined ${invite.counterpartyName ?? "the team"}.`, "success"),
      onError: (err) => setError(err instanceof Error ? err.message : "Try again."),
    });
  }
  function onDecline() {
    setError(null);
    decline.mutate(inviteId, { onError: (err) => setError(err instanceof Error ? err.message : "Try again.") });
  }

  return (
    <li className={styles.row}>
      <Avatar
        initials={tenantInitials(invite.counterpartyName)}
        src={invite.counterpartyPhotoUrl}
        size="md"
        shape="rounded"
      />
      <span className={styles.main}>
        <span className={styles.sentence}>{sentence}</span>
        {expires && <span className={styles.meta}>{expires}</span>}
        {error && (
          <span className={styles.error} role="alert">
            {error}
          </span>
        )}
      </span>
      <span className={styles.actions}>
        <Button size="sm" variant="ghost" disabled={busy} loading={declining} onClick={onDecline}
          aria-label={`Decline: ${sentence}`}>
          Decline
        </Button>
        <Button size="sm" disabled={busy} loading={accepting} onClick={onAccept} aria-label={`Accept: ${sentence}`}>
          Accept
        </Button>
      </span>
    </li>
  );
}
